import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';

const navItems = [
  { label: 'ToStructure', href: '/tostructure' },
  { label: 'ZerveAnalytics', href: '/zerveanalytics' },
  { label: 'Features', href: '#features' },
  { label: 'Tech', href: '#tech' },
  { label: 'Consulting', href: '#services' },
  { label: 'About', href: '#about' },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setDrawerOpen(false);
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled ? 'rgba(12, 12, 12, 0.9)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.06)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Bricolage Grotesque", sans-serif',
                fontWeight: 800,
                fontSize: '1.3rem',
                color: '#ffffff',
                cursor: 'pointer',
                flexGrow: { xs: 1, md: 0 },
                mr: { md: 6 },
                letterSpacing: '-0.02em',
              }}
              onClick={() => {
                if (location.pathname !== '/') {
                  navigate('/');
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              ZerveMe
            </Typography>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flexGrow: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  onClick={() => handleNavClick(item.href)}
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    px: 2,
                    '&:hover': { color: '#ffffff', bgcolor: 'transparent' },
                    transition: 'color 0.2s ease',
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            <Button
              variant="outlined"
              onClick={() => handleNavClick('#contact')}
              sx={{
                display: { xs: 'none', md: 'flex' },
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                fontSize: '0.85rem',
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#ffffff',
                  bgcolor: 'transparent',
                },
              }}
            >
              Get In Touch
            </Button>

            <IconButton
              sx={{ display: { md: 'none' }, color: '#ffffff' }}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#0c0c0c',
            width: 280,
            borderLeft: '1px solid rgba(255, 255, 255, 0.06)',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => handleNavClick(item.href)}
                sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: 500, color: 'text.secondary' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding sx={{ px: 2, mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleNavClick('#contact')}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                '&:hover': { borderColor: '#ffffff' },
              }}
            >
              Get In Touch
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
